import { type UseQueryResult, useQuery } from 'react-query';
import { isEqual, isArray } from 'lodash';

import type { IMessage, IMessageAttachment } from 'src/services/api/messages/types';
import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_MESSAGES_PATH } from 'src/services/api/messages/constants';

type IGetLeadMessagesResponse = {
    messages: IMessage[];
}

export type IGetLeadMessagesWithAttachmentsResponse = IGetLeadMessagesResponse & {
    attachments?: IMessageAttachment[];
}

type IGetLeadMessagesOptions ={
    isArchived?: boolean;
    refetchInterval?: number;
    include_attachments?: boolean;
}

export function useLeadMessages(
    leadUuid: string, options: IGetLeadMessagesOptions & { include_attachments: true }
): UseQueryResult<IGetLeadMessagesWithAttachmentsResponse>;
export function useLeadMessages(
    leadUuid: string, options?: IGetLeadMessagesOptions
): UseQueryResult<IMessage[]>;

export function useLeadMessages(
    leadUuid: string,
    options?: IGetLeadMessagesOptions,
) {
    return useQuery(
        [queryKeys.messages, leadUuid],
        async () => {
            const searchParams = new URLSearchParams();
            if (options?.isArchived != null) {
                searchParams.append('is_archived', options.isArchived.toString());
            }
            if (options?.include_attachments) {
                searchParams.append('include_attachments', 'true');
            }

            searchParams.append('lead_uuid', leadUuid);
            const res = await apiClient(GET_MESSAGES_PATH, { searchParams });

            if (options?.include_attachments) {
                return res.json<IGetLeadMessagesWithAttachmentsResponse>();
            } else {
                return (await res.json<IGetLeadMessagesResponse>()).messages;
            }
        }, {
            enabled: !!leadUuid,
            refetchInterval: options?.refetchInterval,
            isDataEqual(prevData: IGetLeadMessagesWithAttachmentsResponse | IMessage[] | undefined, newData: IGetLeadMessagesWithAttachmentsResponse | IMessage[]) {
                if (!prevData) {
                    return false;
                }
                if (isArray(prevData) && isArray(newData)) {
                    return isEqual(prevData, newData);
                }
                // Compare only ids for attachments because presigned url are always different
                return isEqual(
                    (prevData as IGetLeadMessagesWithAttachmentsResponse).messages,
                    (newData as IGetLeadMessagesWithAttachmentsResponse).messages
                ) && isEqual(
                    (prevData as IGetLeadMessagesWithAttachmentsResponse).attachments?.map(({ id }) => id),
                    (newData as IGetLeadMessagesWithAttachmentsResponse).attachments?.map(({ id }) => id)
                );
            }
        },
    );
}
